import { useEffect, useMemo, useRef } from 'react';
import { formOptions, useStore } from '@tanstack/react-form';
import {
  Alert,
  Button,
  Spinner,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useQuoteForm } from '@/features/quotes/hooks/common/use-quote-form.hook';
import { useRdProposal } from '@/features/quotes/hooks/rd-equipamentos/use-rd-proposal.hook';
import { useSubmitRdCliente } from '@/features/quotes/hooks/rd-equipamentos/use-submit-rd-cliente.hook';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import type { StepClienteValues } from '@/features/quotes/schemas/rd-equipamentos/step-cliente.schema';
import type { RdQuoteStepProps } from '@/features/quotes/components/rd-equipamentos/rd-quote-step.types';
import { useFetchZipcode } from '@/shared/hooks/use-fetch-zipcode.hook';
import {
  validateCnpj,
  validateCpf,
  validateEmail,
  validatePhone,
} from '@/shared/utils';
import { useBrokerContextSafe } from '@/app/context/broker/broker.provider';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { lookupCep } from '@/features/quotes/utils/rd-cep-lookup.util';

import {
  addOneYearMinusDay,
  diffInDays,
  parseDateOnly,
  RD_MAX_VALIDITY_DAYS,
  startOfToday,
} from './utils/step-helpers.util';
import { StepActions } from './sections/step-actions.component';

/** Espelha `Proposal.broker_name` no motor (max_length=128). */
const RD_BROKER_NAME_MAX_LENGTH = 128;

// Frota e LMI Único ficam fora da seleção por enquanto — sobem em um
// próximo momento. Só "Individual" é oferecido até lá.
const PRODUCT_TYPE_OPTIONS = [
  {
    value: 'individual',
    label: 'Equipamentos — Individual',
    description: 'Cotação por item (Móvel, Estacionário ou Portátil).',
  },
  // {
  //   value: 'frota',
  //   label: 'Frota',
  //   description: 'Cotação agregada por Valor de Risco, LMI e plano.',
  // },
  // {
  //   value: 'lmi_unico',
  //   label: 'LMI Único',
  //   description: 'Risco estacionário agregado com LMI total único.',
  // },
];

const INSURED_TYPE_OPTIONS = [
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'cpf', label: 'CPF' },
];

/**
 * Tipo de seguro que a seguradora NÃO aceita. Existe só na tela: o motor
 * conhece quatro `policy_type` e este não é um deles
 * (`ProposalClientIn.known_policy_type`). Ele é oferecido na lista de
 * propósito — antes ficava simplesmente ausente, e o corretor não tinha como
 * saber se era recusa ou esquecimento. Escolhê-lo trava a etapa e nunca vira
 * payload.
 */
const RD_POLICY_TYPE_BLOCKED = 'renovacao_externa_com_sinistro';

/** Mensagem única do bloqueio — usada no campo e no alerta. */
const RD_POLICY_TYPE_BLOCKED_MESSAGE =
  'Renovação externa com sinistro sem aceitação.';

const POLICY_TYPE_OPTIONS = [
  { value: 'novo', label: 'Novo' },
  { value: 'renovacao_interna', label: 'Renovação Interna' },
  {
    value: 'renovacao_externa_sem_sinistro',
    label: 'Renovação Externa sem Sinistro',
  },
  {
    value: 'renovacao_interna_com_sinistro',
    label: 'Renovação Interna com Sinistro',
  },
  {
    value: RD_POLICY_TYPE_BLOCKED,
    label: 'Renovação Externa com Sinistro',
  },
];

const formOpts = formOptions({
  defaultValues: {
    productType: 'individual' as StepClienteValues['productType'],
    insuredType: 'cnpj' as StepClienteValues['insuredType'],
    insuredDoc: '',
    insuredName: '',
    insuredSocialName: '',
    insuredRg: '',
    insuredRgIssuer: '',
    insuredRgDate: '',
    insuredPhone: '',
    insuredLandline: '',
    insuredEmail: '',
    brokerName: '',
    // Alargado com a sentinela recusada: o valor existe no formulário, nunca no
    // payload (ver `RD_POLICY_TYPE_BLOCKED`).
    policyType: 'novo' as
      | StepClienteValues['policyType']
      | typeof RD_POLICY_TYPE_BLOCKED,
    leasedToThirdParties: false,
    previousPolicy: '',
    mailCep: '',
    mailAddress: '',
    mailNumber: '',
    mailComplement: '',
    mailDistrict: '',
    mailCity: '',
    mailState: '',
    coverageScope:
      'Território Brasileiro' as StepClienteValues['coverageScope'],
    validityStart: '',
    validityEnd: '',
  },
});

export function StepCliente({
  onNext,
  onBack,
  canGoBack = true,
  isViewMode = false,
}: RdQuoteStepProps) {
  const form = useQuoteForm({ ...formOpts });

  const { id, policyId } = useQuoteParams();
  const resolvedProposalId = (id || policyId || '').trim();
  const { data: proposal, isLoading: isProposalLoading } = useRdProposal(
    resolvedProposalId || undefined
  );

  const { handleSubmit, isPending, hasProposalId } = useSubmitRdCliente({
    form,
    onNext,
  });

  const { setFieldValue } = form;
  const hasPrefilledRef = useRef(false);

  useEffect(() => {
    if (!proposal || hasPrefilledRef.current) return;
    hasPrefilledRef.current = true;

    setFieldValue('productType', proposal.productType);
    setFieldValue('insuredType', proposal.insuredType);
    setFieldValue('insuredDoc', proposal.insuredDoc);
    setFieldValue('insuredName', proposal.insuredName);
    setFieldValue('insuredSocialName', proposal.insuredSocialName ?? '');
    setFieldValue('insuredRg', proposal.insuredRg ?? '');
    setFieldValue('insuredRgIssuer', proposal.insuredRgIssuer ?? '');
    setFieldValue('insuredRgDate', proposal.insuredRgDate ?? '');
    setFieldValue('insuredPhone', proposal.insuredPhone ?? '');
    setFieldValue('insuredLandline', proposal.insuredLandline ?? '');
    setFieldValue('insuredEmail', proposal.insuredEmail ?? '');
    setFieldValue('brokerName', proposal.brokerName ?? '');
    setFieldValue('policyType', proposal.policyType);
    setFieldValue('leasedToThirdParties', !!proposal.leasedToThirdParties);
    setFieldValue('previousPolicy', proposal.previousPolicy ?? '');
    setFieldValue('mailCep', proposal.mailCep ?? '');
    setFieldValue('mailAddress', proposal.mailAddress ?? '');
    setFieldValue('mailNumber', proposal.mailNumber ?? '');
    setFieldValue('mailComplement', proposal.mailComplement ?? '');
    setFieldValue('mailDistrict', proposal.mailDistrict ?? '');
    setFieldValue('mailCity', proposal.mailCity ?? '');
    setFieldValue('mailState', proposal.mailState ?? '');
    // `coverageScope` NÃO é reidratado: o campo saiu da tela e o produto é
    // sempre Território Brasileiro (default do formulário). Reidratar faria
    // uma proposta legada com Mercosul reenviar Mercosul por um campo que
    // ninguém mais vê.

    setFieldValue('validityStart', (proposal.validityStart || '').slice(0, 10));
    setFieldValue('validityEnd', (proposal.validityEnd || '').slice(0, 10));
  }, [proposal, setFieldValue]);

  /**
   * "Corretora / Filial" nasce com o nome da corretora selecionada no header —
   * a MESMA que `useCreateRdProposal` manda como `partyCode` e que vira o
   * `owner_broker_ref` da proposta. Antes era texto livre e chegava vazio na
   * maioria das cotações, a ponto de as listas unificadas terem de resolver o
   * nome pelo `ownerBrokerRef` para exibir alguma coisa.
   *
   * Só preenche o que está VAZIO: numa proposta já gravada, o valor
   * registrado manda. Depende do prefill ter rodado (ou de não haver proposta),
   * senão sobrescreveria o valor salvo antes de ele chegar.
   */
  const brokerContext = useBrokerContextSafe();
  const selectedBrokerName = brokerContext?.selectedBroker?.name ?? '';

  useEffect(() => {
    if (!selectedBrokerName) return;
    if (hasProposalId && !hasPrefilledRef.current) return;
    if (form.state.values.brokerName?.trim()) return;
    setFieldValue('brokerName', selectedBrokerName);
  }, [selectedBrokerName, hasProposalId, proposal, form, setFieldValue]);

  // O CEP de risco não é mais buscado aqui — o endereço vive no Step 2.
  const { mutateAsync: fetchMailZipcode, isPending: isFetchingMailCep } =
    useFetchZipcode('billing');

  const mailNumberContainerRef = useRef<HTMLDivElement>(null);

  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const validityStartValue = useStore(
    form.store,
    state => state.values.validityStart
  );
  const policyTypeValue = useStore(
    form.store,
    state => state.values.policyType
  );
  /** Tipo de seguro recusado — trava o avanço da etapa (ver `RD_POLICY_TYPE_BLOCKED`). */
  const policyTypeRecusado = policyTypeValue === RD_POLICY_TYPE_BLOCKED;

  const blockInteraction = isPending || isProposalLoading;
  const disabled = isViewMode || blockInteraction;

  /**
   * Segunda trava do tipo de seguro recusado. O `disabled` do botão é a
   * primeira; esta existe porque `handleSubmit` mora no hook, que não conhece
   * a sentinela — sem ela, um caminho que contornasse o `disabled` mandaria ao
   * backend um `policy_type` que o motor rejeita com 422.
   */
  function handleNext() {
    if (policyTypeRecusado) return;
    handleSubmit();
  }

  return (
    <div className='relative flex flex-col gap-8'>
      {isProposalLoading ? (
        <div
          className='absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/80'
          aria-busy
          aria-label='Carregando dados da proposta'
        >
          <Spinner size='md' />
          <Text className='text-sm text-alba-black-400'>
            Carregando dados da proposta...
          </Text>
        </div>
      ) : null}

      {/* Produto */}
      <div className='flex flex-col gap-4'>
        <Text className='text-lg font-bold text-alba-black-500'>Produto</Text>
        <form.AppField
          name='productType'
          children={field => (
            <field.RadioCardField
              options={PRODUCT_TYPE_OPTIONS}
              required
              disabled={disabled || hasProposalId}
              // Ao editar, exibe o produto real da proposta diretamente —
              // não depende do timing do prefill assíncrono (efeito acima),
              // que só atualiza o valor do form após o fetch da proposta.
              value={
                hasProposalId && proposal ? proposal.productType : undefined
              }
              helperText={
                hasProposalId
                  ? 'O produto não pode ser alterado ao editar; crie uma nova proposta para trocá-lo.'
                  : undefined
              }
            />
          )}
        />
      </div>

      {/* Identificação do Segurado */}
      <div className='flex flex-col gap-6'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Identificação do Segurado
        </Text>

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
          <form.AppField
            name='insuredType'
            children={insuredTypeField => (
              <insuredTypeField.SelectField
                label='Tipo de documento'
                required
                options={INSURED_TYPE_OPTIONS}
                disabled={disabled}
                onValueChange={() => form.validateField('insuredDoc', 'change')}
              />
            )}
          />
          <form.Subscribe
            selector={state => state.values.insuredType}
            children={insuredType => (
              <form.AppField
                name='insuredDoc'
                validators={{
                  onChange: ({ value, fieldApi }) => {
                    const stringValue = String(value || '').trim();
                    if (!stringValue) return 'Informe o documento';
                    const type = fieldApi.form.getFieldValue('insuredType');
                    if (type === 'cpf' && !validateCpf(stringValue)) {
                      return 'CPF inválido';
                    }
                    if (type === 'cnpj' && !validateCnpj(stringValue)) {
                      return 'CNPJ inválido';
                    }
                    return undefined;
                  },
                }}
                children={field => (
                  <field.TextField
                    label='Documento'
                    required
                    mask={insuredType === 'cpf' ? 'cpf' : 'cnpj'}
                    maxLength={
                      insuredType === 'cpf'
                        ? FIELD_MAX_LENGTHS.cpf
                        : FIELD_MAX_LENGTHS.cnpj
                    }
                    placeholder={
                      insuredType === 'cpf'
                        ? '000.000.000-00'
                        : '00.000.000/0000-00'
                    }
                    disabled={disabled}
                  />
                )}
              />
            )}
          />
          <form.AppField
            name='insuredPhone'
            validators={{
              onChange: ({ value }) => {
                const digitos = (value || '').replace(/\D/g, '');
                if (!digitos) return 'Informe o celular';
                // `validatePhone` aceita fixo (10) e celular (11); aqui o campo
                // é celular, então exigimos os 11 — ele ainda cobre DDD,
                // repetição e o 9 na terceira posição.
                if (digitos.length !== 11 || !validatePhone(value)) {
                  return 'Celular inválido';
                }
                return undefined;
              },
            }}
            children={field => (
              <field.TextField
                label='Celular'
                required
                mask='phone'
                maxLength={FIELD_MAX_LENGTHS.phone}
                placeholder='(00) 00000-0000'
                disabled={disabled}
              />
            )}
          />
        </div>

        {/*
          Celular e telefone fixo são campos SEPARADOS no documento
          oficial (a apólice tem as duas colunas), e o e-mail é o bloco
          E-MAIL da proposta — os três saíam vazios porque o RD nunca
          perguntou. Celular e e-mail passaram a ser obrigatórios por isso;
          o fixo continua opcional.
        */}
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <form.AppField
            name='insuredLandline'
            children={field => (
              <field.TextField
                label='Telefone fixo'
                // Máscara literal, não o preset `phone`: o preset é
                // `(99) 99999-9999`, de CELULAR (9 dígitos). Fixo tem 8, e
                // com o preset o número ficava um dígito curto de fechar —
                // saindo malformado no documento oficial. O preset é
                // compartilhado com outros produtos, então a correção fica
                // aqui, no campo, sem tocar nele.
                mask='(99) 9999-9999'
                maxLength={FIELD_MAX_LENGTHS.phone}
                placeholder='(00) 0000-0000'
                disabled={disabled}
              />
            )}
          />
          <form.AppField
            name='insuredEmail'
            validators={{
              onChange: ({ value }) => {
                const email = (value || '').trim();
                if (!email) return 'Informe o e-mail';
                return validateEmail(email) ? undefined : 'E-mail inválido';
              },
            }}
            children={field => (
              <field.TextField
                label='E-mail'
                lowercase
                required
                // 160 é a largura da coluna no Motor, não a do padrão de
                // e-mail (254): o passo Cliente é recusado acima disso.
                // Melhor travar a digitação do que devolver 422 no submit.
                maxLength={160}
                placeholder='contato@empresa.com.br'
                disabled={disabled}
              />
            )}
          />
        </div>

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <form.AppField
            name='insuredName'
            validators={{
              onChange: ({ value }) =>
                !value || value.trim() === ''
                  ? 'Informe o nome do segurado'
                  : undefined,
            }}
            children={field => (
              <field.TextField
                label='Nome do segurado'
                required
                maxLength={FIELD_MAX_LENGTHS.personName}
                disabled={disabled}
              />
            )}
          />
          <form.AppField
            name='brokerName'
            children={field => (
              <field.TextField
                label='Corretora / Filial'
                // 128, não `FIELD_MAX_LENGTHS.companyName` (150): o limite
                // real é o do motor (`Proposal.broker_name`, max_length=128).
                // A constante compartilhada não muda porque outros produtos
                // dependem dela.
                maxLength={RD_BROKER_NAME_MAX_LENGTH}
                // Sempre desabilitado, como o fim de vigência: o valor é
                // DERIVADO da corretora selecionada no header — a mesma que
                // vira `partyCode` na criação e `owner_broker_ref` da
                // proposta. Editá-lo aqui só produzia um nome no documento
                // que não corresponde ao dono da cotação.
                disabled
              />
            )}
          />
        </div>

        <form.Subscribe
          selector={state => state.values.insuredType}
          children={insuredType =>
            insuredType === 'cpf' ? (
              <div className='flex flex-col gap-6'>
                <form.AppField
                  name='insuredSocialName'
                  children={field => (
                    <field.TextField
                      label='Nome social'
                      maxLength={FIELD_MAX_LENGTHS.socialName}
                      disabled={disabled}
                    />
                  )}
                />
                <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
                  <form.AppField
                    name='insuredRg'
                    children={field => (
                      <field.TextField label='RG' disabled={disabled} />
                    )}
                  />
                  <form.AppField
                    name='insuredRgIssuer'
                    children={field => (
                      <field.TextField
                        label='Órgão emissor'
                        disabled={disabled}
                      />
                    )}
                  />
                  <form.AppField
                    name='insuredRgDate'
                    children={field => (
                      <field.CalendarField
                        label='Data de emissão'
                        disabled={disabled}
                      />
                    )}
                  />
                </div>
              </div>
            ) : null
          }
        />

        {/* Endereço de correspondência do segurado. Vivia num acordeão
            separado, depois da vigência; passou a ser uma subseção da
            identificação, logo abaixo de nome e documento. Segue inteiramente
            opcional. */}
        <div className='flex flex-col gap-6'>
          <Text className='text-lg font-bold text-alba-black-500'>
            Endereço de correspondência (opcional)
          </Text>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-3'>
            <form.AppField
              name='mailCep'
              listeners={{
                onBlur: ({ value }) =>
                  lookupCep({
                    zipcode: value,
                    fetchFn: fetchMailZipcode,
                    policyId: resolvedProposalId || undefined,
                    fields: {
                      address: 'mailAddress',
                      district: 'mailDistrict',
                      city: 'mailCity',
                      state: 'mailState',
                    },
                    setFieldValue,
                    onFilled: () =>
                      mailNumberContainerRef.current
                        ?.querySelector('input')
                        ?.focus(),
                  }),
              }}
              children={field => (
                <field.TextField
                  label='CEP'
                  mask='cep'
                  maxLength={FIELD_MAX_LENGTHS.cep}
                  placeholder='00000-000'
                  helperText='Deixe em branco para usar o endereço de risco.'
                  disabled={disabled || isFetchingMailCep}
                />
              )}
            />
            <div ref={mailNumberContainerRef}>
              <form.AppField
                name='mailNumber'
                children={field => (
                  <field.TextField
                    label='Número'
                    maxLength={FIELD_MAX_LENGTHS.addressNumber}
                    disabled={disabled}
                  />
                )}
              />
            </div>
            <form.AppField
              name='mailComplement'
              children={field => (
                <field.TextField
                  label='Complemento'
                  maxLength={FIELD_MAX_LENGTHS.complement}
                  disabled={disabled}
                />
              )}
            />
          </div>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <form.AppField
              name='mailAddress'
              children={field => (
                <field.TextField
                  label='Endereço'
                  maxLength={FIELD_MAX_LENGTHS.street}
                  disabled={disabled}
                />
              )}
            />
            <form.AppField
              name='mailDistrict'
              children={field => (
                <field.TextField
                  label='Bairro'
                  maxLength={FIELD_MAX_LENGTHS.neighborhood}
                  disabled={disabled}
                />
              )}
            />
          </div>
          <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
            <form.AppField
              name='mailCity'
              children={field => (
                <field.TextField
                  label='Cidade'
                  maxLength={FIELD_MAX_LENGTHS.city}
                  disabled={disabled}
                />
              )}
            />
            <form.AppField
              name='mailState'
              children={field => (
                <field.TextField
                  label='UF'
                  maxLength={FIELD_MAX_LENGTHS.state}
                  disabled={disabled}
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Tipo de Seguro */}
      <div className='flex flex-col gap-6'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Tipo de Seguro
        </Text>
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <form.AppField
            name='policyType'
            validators={{
              onChange: ({ value }) =>
                value === RD_POLICY_TYPE_BLOCKED
                  ? RD_POLICY_TYPE_BLOCKED_MESSAGE
                  : undefined,
            }}
            children={field => (
              <field.SelectField
                label='Tipo de seguro'
                required
                options={POLICY_TYPE_OPTIONS}
                disabled={disabled}
              />
            )}
          />
          <form.Subscribe
            selector={state => state.values.policyType}
            children={policyType =>
              policyType !== 'novo' && policyType !== RD_POLICY_TYPE_BLOCKED ? (
                <form.AppField
                  name='previousPolicy'
                  children={field => (
                    <field.TextField
                      label='Número da apólice anterior'
                      maxLength={FIELD_MAX_LENGTHS.policyNumber}
                      disabled={disabled}
                    />
                  )}
                />
              ) : null
            }
          />
        </div>

        {/* Atributo de subscrição (PO 2026-07-20): não altera o cálculo —
            aciona as cláusulas obrigatórias da apólice. Deixou de tirar a
            cotação da aprovação automática: o referral saiu do motor
            (`Pricing._leased_referral`) a pedido do PO. */}
        <div className='flex flex-col gap-1'>
          <form.AppField
            name='leasedToThirdParties'
            children={field => (
              <field.CheckboxField
                label='Equipamento(s) locado(s), arrendado(s) ou cedido(s) a terceiros'
                disabled={disabled}
              />
            )}
          />
          <Text className='text-xs text-alba-gray-500'>
            Aciona cláusulas obrigatórias — não altera o prêmio.
          </Text>
        </div>

        {/* O botão desabilitado sozinho não explica nada: o alerta diz por que
            a etapa não avança, e some assim que o tipo de seguro muda. */}
        {policyTypeRecusado ? (
          <Alert.Root status='error'>
            <Alert.Icon />
            <Alert.Content>
              <Alert.Title>Tipo de seguro sem aceitação</Alert.Title>
              <Alert.Description>
                {RD_POLICY_TYPE_BLOCKED_MESSAGE} Escolha outro tipo de seguro
                para continuar.
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        ) : null}
      </div>

      {/* Cobertura e vigência. O Local de Risco saiu daqui: o endereço
          é capturado no Step 2, junto do risco — por item no fluxo
          individual, na proposta no LMI Único (spec de 2026-07-19). */}
      <div className='flex flex-col gap-6'>
        <Text className='text-lg font-bold text-alba-black-500'>
          Cobertura e vigência
        </Text>
        {/* O âmbito não é mais perguntado: o produto é sempre Território
            Brasileiro. O valor continua indo no payload (o BFF o exige) a
            partir do default do formulário. */}
        <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
          <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
            <form.AppField
              name='validityStart'
              validators={{
                onChange: ({ value }) => {
                  if (!value) return 'Informe o início de vigência';
                  if (parseDateOnly(value) < startOfToday()) {
                    return 'Início de vigência retroativo';
                  }
                  return undefined;
                },
              }}
              listeners={{
                // A vigência do produto é sempre de um ano: o fim é derivado
                // aqui e o campo correspondente é somente-leitura. Início
                // vazio limpa o fim — deixá-lo preenchido num campo que o
                // usuário não pode mais corrigir seria um dado órfão.
                onChange: ({ value }) => {
                  setFieldValue('validityEnd', addOneYearMinusDay(value));
                  form.validateField('validityEnd', 'change');
                },
              }}
              children={field => (
                <field.CalendarField
                  label='Início de vigência'
                  required
                  minDate={todayIso}
                  disabled={disabled}
                />
              )}
            />
            <form.AppField
              name='validityEnd'
              validators={{
                onChange: ({ value, fieldApi }) => {
                  if (!value) return 'Informe o fim de vigência';
                  const startValue =
                    fieldApi.form.getFieldValue('validityStart');
                  if (!startValue) return undefined;
                  const start = parseDateOnly(startValue);
                  const end = parseDateOnly(value);
                  if (end < start) {
                    return 'Vigência final anterior à inicial';
                  }
                  if (diffInDays(start, end) > RD_MAX_VALIDITY_DAYS) {
                    return 'Vigência superior a 366 dias';
                  }
                  return undefined;
                },
              }}
              children={field => (
                <field.CalendarField
                  label='Fim de vigência'
                  required
                  minDate={validityStartValue || todayIso}
                  // Sempre desabilitado: derivado do início (+1 ano, mesmo dia).
                  disabled
                />
              )}
            />
          </div>
        </div>
      </div>

      {/* Ações */}
      <StepActions>
        <Button
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={isViewMode || !canGoBack || blockInteraction}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <Button
          variant='primary'
          size='md'
          className='flex items-center gap-2'
          onClick={handleNext}
          disabled={isViewMode || blockInteraction || policyTypeRecusado}
        >
          {isPending ? (
            <>
              Processando...
              <Spinner size='sm' colorScheme='gray' />
            </>
          ) : (
            <>
              Próximo
              <ArrowRight className='h-6 w-6' strokeWidth={2} />
            </>
          )}
        </Button>
      </StepActions>
    </div>
  );
}
